import { useEffect, useRef, useState } from "react";

const expertise = [
  {
    icon: "ri-global-line",
    category: "Trade Compliance",
    title: "International Trade & Regulatory Affairs",
    description: "Navigating the complex landscape of customs regulations, import/export compliance, and international trade law with precision and strategic foresight.",
    bullets: [
      "Customs classification & tariff analysis",
      "Import/export documentation & compliance",
      "19 CFR standards & USMCA programs",
      "Trade risk assessment & mitigation",
    ],
  },
  {
    icon: "ri-flow-chart",
    category: "Process Architecture",
    title: "Process Improvement & Operations",
    description: "Applying Six Sigma methodology and systems thinking to identify inefficiencies, redesign workflows, and deliver measurable operational improvements.",
    bullets: [
      "Six Sigma Black Belt methodology",
      "Inventory & documentation management",
      "Excel pivot tables & data analysis",
      "Cross-functional team leadership",
    ],
  },
  {
    icon: "ri-shield-keyhole-line",
    category: "Cybersecurity & IT",
    title: "Technology & Security Systems",
    description: "Bridging operations and technology — from network security and IT infrastructure to data analysis — building smarter, more resilient systems.",
    bullets: [
      "Network traffic analysis (Wireshark, Splunk)",
      "Cybersecurity standards: PCI DSS, GDPR, SOX",
      "Python scripting & data analysis",
      "Cisco networking & cloud tools",
    ],
  },
];

const timeline = [
  {
    period: "Present",
    title: "Customs & Trade Compliance Specialist",
    company: "Stäubli",
    location: "Duncan, SC",
    bullets: [
      "Support customs classification, valuation alignment, duty-risk review, and process documentation across cross-functional teams.",
      "Build practical tools and reference materials that translate trade compliance requirements into clearer business processes.",
    ],
    current: true,
  },
  {
    period: "06/2024 – 01/2026",
    title: "Global Trade Compliance Specialist",
    company: "Allyn International",
    location: "Greenville, SC",
    bullets: [
      "Classify and provide valuation for furniture products for the specialist account.",
      "Research and use 19 CFR standards and regulations to determine applicability of special program indicators for USMCA.",
      "Use Allyn-created program CIMS to provide information to brokers to facilitate the processing of entries.",
      "Collaborate with management to facilitate learning and foster personal and professional growth to meet KPI and client needs.",
    ],
    current: false,
  },
  {
    period: "08/2023 – 12/2023",
    title: "SOC Analyst I Intern (200 Hours)",
    company: "Carolina Cyber Center",
    location: "Raleigh, NC",
    bullets: [
      "Enhanced cybersecurity skills using Wireshark and Splunk for network traffic analysis and security event management.",
      "Gained experience with commercial and open-source security products, scanning tools, firewalls, and Linux-based systems.",
      "Developed understanding of cybersecurity standards (PCI DSS, GDPR, SOX, GLBA) critical for Governance, Risk, and Compliance.",
      "Practiced using nmap for network scanning and mapping, gaining insights into threat intelligence.",
    ],
    current: false,
  },
  {
    period: "07/2021 – 03/2024",
    title: "Logistics Coordinator",
    company: "NORCA Engineered Products",
    location: "Raleigh, NC",
    bullets: [
      "Maintained inventory documentation for monthly shipments valued at $2M.",
      "Reconciled over 5,000 parts across warehouses to improve inventory management.",
      "Developed Excel pivot tables and formulas to streamline reordering, boosting efficiency by 10%.",
    ],
    current: false,
  },
  {
    period: "05/2019 – 07/2021",
    title: "Disability Analyst",
    company: "Disability Determination Services",
    location: "Raleigh, NC",
    bullets: [
      "Reviewed 1,000+ pages of medical evidence weekly to determine eligibility for disability benefits.",
      "Proposed functional capacity statements to assess impairment severity.",
      "Contacted claimants to obtain statements and treatment information to inform benefits decisions.",
    ],
    current: false,
  },
  {
    period: "05/2018 – 05/2019",
    title: "Logistics Specialist",
    company: "Allyn International",
    location: "Greenville, SC",
    bullets: [
      "Created documentation for 100–250 monthly shipments, ensuring timely delivery.",
      "Tracked specialized shipments to guarantee secure handling and personalized attention.",
      "Coordinated 500+ incoming and outgoing shipments per month.",
      "Partnered with 100+ customers to integrate processes and resolve issues.",
    ],
    current: false,
  },
];

const skills = [
  "Network Traffic Analysis (Wireshark)",
  "Customs Compliance",
  "Regulatory Compliance",
  "Process Improvement",
  "Data Analysis (Excel Pivot Tables)",
  "Microsoft Office 365",
  "Python",
  "Documentation Management",
];

export default function Work() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="work" className="py-28 md:py-36 bg-[#FAF7F2]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Section label */}
        <div className={`flex items-center gap-4 mb-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]" style={{ fontFamily: "'Lato', sans-serif" }}>
            02 / Work
          </span>
          <div className="flex-1 h-px bg-[#C9A961]/25" />
        </div>

        <div className={`mb-16 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2
            className="font-serif text-4xl md:text-5xl text-[#2B1810] leading-tight mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.01em" }}
          >
            Where Structure Meets Strategy
          </h2>
          <p className="text-base text-[#B8A5A8] max-w-xl" style={{ fontFamily: "'Lato', sans-serif" }}>
            A decade of experience across trade compliance, operations, cybersecurity, and technology — always with an eye toward clarity and impact.
          </p>
        </div>

        {/* Expertise Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
          {expertise.map((item, i) => (
            <div
              key={item.category}
              className={`group bg-white rounded-2xl p-8 border border-[#C9A961]/10 hover:border-[#C9A961]/40 hover:-translate-y-2 transition-all duration-300 cursor-default ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${200 + i * 100}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#C9A961]/15 mb-6 group-hover:bg-[#C9A961]/25 transition-colors duration-300">
                <i className={`${item.icon} text-[#2B1810] text-xl`} />
              </div>
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961] mb-2 block" style={{ fontFamily: "'Lato', sans-serif" }}>
                {item.category}
              </span>
              <h3 className="font-serif text-xl text-[#2B1810] mb-3 leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {item.title}
              </h3>
              <p className="text-sm text-[#2B1810]/60 leading-relaxed mb-6" style={{ fontFamily: "'Lato', sans-serif" }}>
                {item.description}
              </p>
              <ul className="space-y-2">
                {item.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-[13px] text-[#2B1810]/70" style={{ fontFamily: "'Lato', sans-serif" }}>
                    <span className="w-1 h-1 rounded-full bg-[#C9A961] mt-2 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Skills strip */}
        <div className={`mb-24 transition-all duration-700 delay-400 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] mb-5" style={{ fontFamily: "'Lato', sans-serif" }}>
            Core Skills
          </p>
          <div className="flex flex-wrap gap-3">
            {skills.map((s) => (
              <span
                key={s}
                className="px-4 py-2 rounded-full border border-[#C9A961]/25 text-[13px] text-[#2B1810] bg-white hover:border-[#C9A961]/60 hover:bg-[#C9A961]/5 transition-all duration-300"
                style={{ fontFamily: "'Lato', sans-serif" }}
              >
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className={`transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-4 mb-12">
            <h3 className="font-serif text-3xl text-[#2B1810] whitespace-nowrap" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Professional Journey
            </h3>
            <div className="flex-1 h-px bg-[#C9A961]/25" />
          </div>

          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-[#C9A961]/30 hidden md:block" />
            <div className="space-y-5">
              {timeline.map((item, i) => (
                <div key={i} className="relative flex gap-8 md:pl-16">
                  {/* Timeline node */}
                  <div className={`absolute left-[18px] top-6 w-4 h-4 rounded-full border-2 hidden md:block ${item.current ? "border-[#C9A961] bg-[#C9A961]" : "border-[#C9A961] bg-[#FAF7F2]"}`} />

                  {/* Card */}
                  <div
                    className="flex-1 bg-white rounded-xl border border-[#C9A961]/10 hover:border-[#C9A961]/30 transition-colors duration-300 overflow-hidden cursor-pointer"
                    onClick={() => setExpanded(expanded === i ? null : i)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-6">
                      <div className="flex items-start gap-3">
                        {item.current && (
                          <span className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-[#C9A961] animate-pulse" />
                        )}
                        <div>
                          <h4 className="font-serif text-lg text-[#2B1810] leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                            {item.title}
                          </h4>
                          <p className="text-sm text-[#B8A5A8]" style={{ fontFamily: "'Lato', sans-serif" }}>
                            {item.company} &mdash; {item.location}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-[11px] uppercase tracking-[0.2em] text-[#C9A961]" style={{ fontFamily: "'Lato', sans-serif" }}>
                          {item.period}
                        </span>
                        <i className={`ri-arrow-down-s-line text-[#C9A961] transition-transform duration-300 ${expanded === i ? "rotate-180" : ""}`} />
                      </div>
                    </div>

                    {/* Expandable bullets */}
                    <div className={`transition-all duration-300 overflow-hidden ${expanded === i ? "max-h-96" : "max-h-0"}`}>
                      <ul className="px-6 pb-6 space-y-2 border-t border-[#C9A961]/10 pt-4">
                        {item.bullets.map((b, bi) => (
                          <li key={bi} className="flex items-start gap-3 text-sm text-[#2B1810]/70 leading-relaxed" style={{ fontFamily: "'Lato', sans-serif" }}>
                            <span className="w-1 h-1 rounded-full bg-[#C9A961] mt-2 flex-shrink-0" />
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
